import { createStore, createEvent, createEffect } from 'effector';
import { createTelegramApiClient } from '../utils/apiClient';
import { INVALID_PROMO_LINK_MESSAGE } from '../utils/promoMessages';
import { AuthenticatedRequest } from './types';

// Типы
export interface CampaignSettingsRequest {
  telegramId: string;
  startParam?: string;
}

export interface CampaignSettingsResponse {
  campaignId: number;
  welcomeMessage?: string;
  buttonColor?: string;
  partnerBotUrl?: string;
  welcomeImageUrl?: string;
  mainImageUrl?: string;
  isActive?: boolean | null;
}

export interface CampaignUserSettingsResponse extends CampaignSettingsResponse {
  userExists: boolean;
  phoneExists: boolean;
  termsConfirm: boolean;
  deepLinkCode?: string;
  skipQrScan?: boolean;
  campaignCode?: string;
}

export interface CampaignError {
  message: string;
  isSessionExpired?: boolean;
}

// События
export const setCampaignSettings = createEvent<CampaignUserSettingsResponse>();
export const setCampaignLoading = createEvent<boolean>();
export const setCampaignError = createEvent<string | null>();
export const createUserAutomatically = createEvent<{ username?: string; displayName: string }>();
export const hideTermsAgreement = createEvent();

// Эффекты
export const getCampaignSettingsFx = createEffect<AuthenticatedRequest<CampaignSettingsRequest>, CampaignUserSettingsResponse, CampaignError>(async ({ data, initData }) => {
  console.log('getCampaignSettingsFx started with data:', data);
  
  try {
    const telegramClient = createTelegramApiClient(initData, data.startParam);
    const result = await telegramClient.get(`/campaign/settings`, {
      loadingText: 'Загружаем настройки кампании...'
    });
    
    console.log('getCampaignSettingsFx result:', result);
    return result;
  } catch (error: any) {
    console.log('getCampaignSettingsFx error:', error);
    
    const isSessionExpired = error.message?.toLowerCase().includes('сеанс истек') ||
                            error.message?.toLowerCase().includes('session expired') ||
                            error.message?.toLowerCase().includes('401');
    
    console.log('getCampaignSettingsFx isSessionExpired:', isSessionExpired);
    
    const campaignError: CampaignError = {
      message: error.message || INVALID_PROMO_LINK_MESSAGE,
      isSessionExpired
    };
    throw campaignError;
  }
});

// Тип состояния
export interface CampaignState {
  settings: CampaignUserSettingsResponse | null;
  isLoading: boolean;
  error: string | null;
  isSessionExpired: boolean;
}

// Store
export const campaignStore = createStore<CampaignState>({
  settings: null,
  isLoading: false,
  error: null,
  isSessionExpired: false,
})
  .on(setCampaignSettings, (state, settings) => ({ ...state, settings }))
  .on(setCampaignLoading, (state, isLoading) => ({ ...state, isLoading }))
  .on(setCampaignError, (state, error) => ({ ...state, error }))
  .on(getCampaignSettingsFx.pending, (state, isLoading) => ({ ...state, isLoading }))
  .on(getCampaignSettingsFx.doneData, (state, settings) => {
    console.log('getCampaignSettingsFx.doneData called:', settings);
    
    return {
      ...state,
      settings,
      isLoading: false,
      error: null
    };
  })
  .on(getCampaignSettingsFx.failData, (state, error) => {
    console.log('getCampaignSettingsFx failed with error:', error);
    console.log('isSessionExpired:', error.isSessionExpired);
    return {
      ...state,
      isLoading: false,
      error: error.message,
      isSessionExpired: error.isSessionExpired || false
    };
  });

// Actions
export const getCampaignSettings = createEvent();

getCampaignSettings.watch(() => {
  console.log('getCampaignSettings event triggered');
});
